import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { translate, translateObject } from '@jsverse/transloco';
import moment from 'moment';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { DropdownObject } from '@inmoment/the-kitchen';

import {
  RGBAColor,
  RGBAColorInput,
} from '@inmoment/the-kitchen/src/lib/Pickers/ColorPicker/ColorPicker.model';

import { DATE_WITHOUT_TIMEZONE_FORMAT } from 'app/modules/collection/services/filter.service';
import { ChartCustomColorService } from 'app/modules/dashboard/services/chart-custom-color.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { TimezoneService } from 'app/services/timezone.service';
import { ChartOptionUtils } from 'app/utils/chart-options.utils';
import { nameof, Utils } from 'app/utils/utils';
import { ChartDisplayOptions } from '../../../../models';
import { StorageAccountService } from '../../../../providers/storage-api/blender-account.service';
import { DEFAULT_PROJECT_COLOR_PALETTE } from '../../../../providers/storage-api/project.service';
import {
  ChartAxisValues,
  ChartDateInterval,
  DateMarker,
  WidgetChartOptions,
} from '../widget-chart/widget-chart.component';
import { AbstractChartReport } from '../../services/chart-report/chart-report-abstract/chart-report-abstract';
import { ChartReportService } from '../../services/chart-report.service';

export const MAX_DATE_MARKER = 4;

export interface CustomField {
  name: string;
  type: string;
}

export type ChartGoalShadeDirection = 'none' | 'above' | 'below';

interface DateMarkerColorChangeEvent {
  index: number;
  color: RGBAColorInput;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-chart-options-dialog',
  templateUrl: './chart-options-dialog.component.html',
  styleUrls: ['./chart-options-dialog.component.scss'],
  standalone: false,
})
export class ChartOptionsDialogComponent implements OnInit, OnChanges, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() public data: ChartDisplayOptions;

  @Output() modalClosed: EventEmitter<any> = new EventEmitter<any[]>();

  currentReport: AbstractChartReport;
  chartSettingsOptions: {};
  graphTypeOptions: DropdownObject[];
  aggregatorOptions: DropdownObject[];
  canShowAsStacked: boolean;
  hasTimeInterval: boolean;
  timeIntervalOptions: DropdownObject[] = [];
  timeIntervalOptionsDisabled: boolean;
  canSetChartMaximum: boolean;
  canSetChartMaximumHorizontal: boolean;
  canSetChartMaximumPercentage: boolean;
  canSetChartGoal: boolean;
  canSetDateMarkers: boolean;
  isScatterPlot: boolean;
  errorMessages: Map<string, string> = new Map();
  scatterAxes: DropdownObject[] = [];
  primaryAxisValues: DropdownObject[];
  chartAxisValue: ChartAxisValues;

  canDisplayChartValues: boolean;
  isDisplayChartValuesDisabled: boolean;
  displayChartValuesDisabledTooltip: string;
  canColorByPoint: boolean;
  isColorByPointDisabled: boolean;
  colorByPointDisabledTooltip: string;

  isPriorCurrentVolumeChart: boolean;

  defaultChartGoalColor: RGBAColor = {
    red: 37,
    green: 55,
    blue: 77,
    alpha: 1,
  };
  chartGoalColor: RGBAColor = this.defaultChartGoalColor;
  chartGoalColorChange$ = new Subject<RGBAColor>();
  defaultChartGoalDisplayAs = translate('widgets.options.goal');
  chartGoalDisplayAs = this.defaultChartGoalDisplayAs;
  chartGoalShadeOptions: DropdownObject[];

  dateMarkerForm: FormGroup<{
    dateMarkers: FormArray<
      FormGroup<{
        date: FormControl<Date>;
        displayName: FormControl<string>;
        color: FormControl<RGBAColorInput>;
      }>
    >;
  }>;
  dateMarkerColorChange$ = new Subject<DateMarkerColorChangeEvent>();

  private readonly destroy$ = new Subject<void>();
  projectColors = DEFAULT_PROJECT_COLOR_PALETTE.map((colorString) => ({
    color: ChartCustomColorService.colorStringToRgbaColor(colorString),
  }));
  canShowSecondaryAxis = false;
  chartSecondaryAxisValue: ChartAxisValues;
  secondaryAxisToggleDisabledText: string;
  isSecondaryAxisToggleDisabled = true;
  canShowBucketDisplayedAs = false;
  bucketDisplayedAsOptions: DropdownObject[] = [];
  bucketDisplayedAsSelectedId: 'total' | 'percentage' = 'total';

  constructor(
    private chartReportService: ChartReportService,
    private accountService: StorageAccountService,
    private timezoneService: TimezoneService,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    //So all sentiment volume charts don't need to be made from scratch
    //Fill in the aggregator value so the date interval is selectable.
    if (this.data.payload.type === 'sentimentVolume' && !this.data.payload.aggregator) {
      this.data.payload.aggregator = 'time';
    }

    this.chartAxisValue = this.data.payload.chartAxisValue ?? 'default';
    this.chartSecondaryAxisValue = this.data.payload.chartSecondaryAxisValue ?? 'default';

    this.data.payload.display ??= {};

    this.currentReport = this.getReport();

    if (this.data.widgetGroup?.isImpact) {
      this.isPriorCurrentVolumeChart = true;
    }
    this.chartSettingsOptions = {};
    this.currentReport.getChartSettings().forEach((optGroup) => {
      if (optGroup.type === 'singleSelect') {
        this.chartSettingsOptions[optGroup.property] = optGroup.settings.map((setting) => ({
          title: setting.label,
          id: setting.value,
          subtitle: setting.description,
        }));
      } else if (optGroup.settings) {
        optGroup.settings
          .filter((s) => s.type === 'singleSelect')
          .forEach((s) => {
            this.chartSettingsOptions[optGroup.property + '.' + s.property] = s.options.map(
              (o) => ({
                title: Utils.titleCase(o),
                id: o,
              })
            );
          });
      }
    });

    this.graphTypeOptions = this.currentReport.getGraphTypes().map((chartType) => {
      const chartTypeValidity = this.checkGraphType(chartType);

      return {
        title: this.chartOptionDisplayName(chartType),
        id: chartType,
        disabled: !chartTypeValidity.valid,
        subtitle: chartTypeValidity.message,
      };
    });

    this.aggregatorOptions = this.currentReport.getAggregators().map((agg) => {
      const aggregatorValidity = this.checkAggregator(agg);

      return {
        title: this.chartOptionDisplayName(agg),
        id: agg,
        disabled: !aggregatorValidity.valid,
        subtitle: aggregatorValidity.message,
      };
    });

    this.chartGoalShadeOptions = [
      {
        id: 'none',
        title: translate('widgets.options.none'),
      },
      {
        id: 'above',
        title: translate('widgets.options.above'),
      },
      {
        id: 'below',
        title: translate('widgets.options.below'),
      },
    ];

    this.primaryAxisValues = [
      {
        id: 'default',
        title: translate('widgets.options.default'),
      },
      {
        id: 'custom',
        title: translate('widgets.options.custom'),
      },
    ];

    this.initTimeIntervals(this.currentReport);

    this.canSetDateMarkers = this.currentReport.canSetDateMarkers();
    this.initDateMarkers();

    this.setDisplayChartValuesOptions(this.currentReport);
    this.setColorByPointOptions(this.currentReport);

    this.canShowAsStacked = this.currentReport.canShowAsStacked();
    this.canSetChartMaximum = this.currentReport.canSetChartMaximum();
    this.canSetChartMaximumHorizontal = this.currentReport.canSetChartMaximumHorizontal();
    this.canSetChartMaximumPercentage = this.currentReport.canSetChartMaximumPercentage();
    this.canSetChartGoal = this.currentReport.canSetChartGoal();
    this.canShowSecondaryAxis = this.data.payload.additionalBreakdownOptions;
    if (this.canShowSecondaryAxis) {
      const { breakdownOptions, additionalBreakdownOptions } = this.data.payload;
      this.isSecondaryAxisToggleDisabled =
        additionalBreakdownOptions &&
        breakdownOptions.scoring !== additionalBreakdownOptions.scoring;
      this.secondaryAxisToggleDisabledText = this.isSecondaryAxisToggleDisabled
        ? translate('widgets.options.scoring-methodology-must-be-consistent')
        : '';
    }

    const translations = translate<string[]>([
      'widgets.options.volume',
      'widgets.options.sentiment',
      'widgets.options.frequency',
      'widgets.options.positive-impact',
      'widgets.options.negative-impact',
    ]);

    this.isScatterPlot = this.data.payload.type === 'scatterPlot';
    if (this.isScatterPlot) {
      this.scatterAxes.push({
        title: translations[0],
        id: 'volume',
      });
      this.scatterAxes.push({
        title: translations[1],
        id: 'sentiment',
      });
      this.scatterAxes.push({
        title: translations[2],
        id: 'frequency',
      });

      if (this.data.widgetGroup?.isImpact) {
        this.scatterAxes.push({
          title: translations[3],
          id: 'impact_positive',
        });
        this.scatterAxes.push({
          title: translations[4],
          id: 'impact_negative',
        });
      }
    }

    this.accountService.groupFieldsSub.latest().then((accountMetadata) => {
      const customFields = accountMetadata.metadata.map((f) => ({
        name: f.fieldName,
        type: f.fieldType,
      }));

      this.scatterAxes = [...this.scatterAxes];

      customFields.forEach((f) => {
        if (
          (f.type === 'long' || f.type === 'double') &&
          this.data.widgetDataSource.customFields &&
          this.data.widgetDataSource.customFields.includes(f.name)
        ) {
          this.scatterAxes.push({
            title: FieldService.getFieldLongName(f.name),
            id: f.name,
          });
        }
      });
    });

    this.canSetChartGoal = this.currentReport.canSetChartGoal();

    if (this.data.payload.chartGoalDisplayAs === undefined) {
      this.data.payload.chartGoalDisplayAs = translate('widgets.options.goal');
    }

    if (this.data.payload.chartGoalColor != null) {
      this.chartGoalColor = ChartCustomColorService.colorStringToRgbaColor(
        this.data.payload.chartGoalColor
      );
    } else {
      this.setChartGoalColor(this.defaultChartGoalColor);
    }
    this.chartGoalColorChange$
      .pipe(
        debounceTime(100),
        distinctUntilChanged((prev, curr) => Utils.deepEquals(prev, curr)),
        takeUntil(this.destroy$)
      )
      .subscribe((value) => this.setChartGoalColor(value));

    if (this.data.payload.chartGoalShade === undefined) {
      this.data.payload.chartGoalShade = this.chartGoalShadeOptions[0].id;
    }

    this.canShowBucketDisplayedAs =
      this.data.payload.type === 'breakdown' &&
      (this.data.payload.breakdownOptions?.scoring === 'bucket' ||
        this.data.payload.additionalBreakdownOptions?.scoring === 'bucket');
    if (this.canShowBucketDisplayedAs) {
      this.bucketDisplayedAsOptions = [
        {
          id: 'total',
          title: this.chartOptionDisplayName('total'),
        },
        {
          id: 'percentage',
          title: this.chartOptionDisplayName('percentage'),
        },
      ];
      this.bucketDisplayedAsSelectedId =
        this.data.payload.display?.showAsPercentage === true ? 'percentage' : 'total';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data && this.data.defaultColorPalette) {
      this.projectColors = this.data.defaultColorPalette.map((color) => ({ color }));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initTimeIntervals(report) {
    // Time interval for journey widget is managed by its own controls, not chart options
    if (this.data.payload.type === 'journey') {
      this.hasTimeInterval = false;
      return;
    }
    this.hasTimeInterval = report.hasTimeInterval();
    if (this.hasTimeInterval) {
      this.timeIntervalOptions = ChartDateInterval.map((scale) => ({
        title: this.chartOptionDisplayName(scale),
        id: scale,
        disabled: !this.isDateIntervalEnabled(scale),
        get subtitle() {
          return (
            (this.disabled && translate('widgets.options.not-available-for-current-data')) ||
            undefined
          );
        },
      }));
    }
    this.timeIntervalOptionsDisabled = this.timeIntervalOptions.every((option) => option.disabled);
  }

  public setChartOption(prop: string, value: any, needRefresh = true) {
    const setNestedValue = (obj, path, val) => {
      const [head, ...rest] = path.split('.');

      if (!rest.length) {
        obj[head] = val;
        return;
      }

      setNestedValue(obj[head], rest.join('.'), val);
    };

    setNestedValue(this.data.payload, prop, value);

    this.data.needRefresh ||= needRefresh;

    if (value === 'dependencywheel') {
      this.data.payload.display.showAsPercentage = false;
      this.data.payload.display.decimalPrecision = '0';
    }
  }

  //Temporary fix until Kitchen ticket DEV-61992 fixed
  public onKeyUp(prop: string, event: any) {
    if (!event.target.value) {
      this.setChartOption(prop, null);
    }
  }

  public setScatterAxisChartOption(prop, value) {
    this.setChartOption(prop, value, true);

    if (
      this.data.payload.xAxis === this.data.payload.yAxis ||
      (this.data.payload.xAxis.includes('impact') && this.data.payload.yAxis.includes('impact'))
    ) {
      this.errorMessages.set(
        'scatterAxes',
        translate('widgets.options.scatter-plot.error-axes-different')
      );
    } else {
      this.errorMessages.delete('scatterAxes');
    }
  }

  public validateChartGoalOptions() {
    if (
      this.data.payload.chartMaximum &&
      this.data.payload.chartGoal &&
      +this.data.payload.chartGoal > +this.data.payload.chartMaximum
    ) {
      this.errorMessages.set(
        'goalHigherThanMaximumAxisValue',
        translate('widgets.options.error-goal-higher-maximum')
      );
    } else {
      this.errorMessages.delete('goalHigherThanMaximumAxisValue');
    }
  }

  public hasChartOptionError() {
    return this.errorMessages.size > 0;
  }

  public isChartOptionsApplyDisabled() {
    return (
      (this.hasTimeInterval &&
        !this.timeIntervalOptionsDisabled &&
        !this.data.payload.dateInterval) ||
      this.hasChartOptionError() ||
      this.isDateMarkersInvalid
    );
  }

  public setChartTypeChartOption(value: string) {
    this.setChartOption('graphType', value, true);

    this.aggregatorOptions.forEach((agg) => {
      const aggregatorValidity = this.checkAggregator(agg.id);
      agg.disabled = !aggregatorValidity.valid;
      agg.subtitle = aggregatorValidity.message;
    });

    setTimeout(() => (this.aggregatorOptions = [...this.aggregatorOptions]));

    const currentReport = this.getReport(this.data.payload);
    this.initTimeIntervals(currentReport);
    this.canShowAsStacked = currentReport.canShowAsStacked();
    this.canSetChartMaximum = currentReport.canSetChartMaximum();
    this.canSetChartGoal = currentReport.canSetChartGoal();

    this.setDisplayChartValuesOptions(currentReport);
    this.setColorByPointOptions(currentReport);

    this.setDateMarkers(currentReport);
  }

  public setBucketDisplayedAsChartOption(value: string): void {
    const selectedValue: 'total' | 'percentage' = value === 'percentage' ? 'percentage' : 'total';
    this.bucketDisplayedAsSelectedId = selectedValue;
    this.setChartOption('display.showAsPercentage', selectedValue === 'percentage', true);
  }

  public setAggregatorChartOption(value: string) {
    this.setChartOption('aggregator', value, true);
    // If aggregator is not total or show by percentage we clear the stacked option so we don't get total volume to the chart
    if (value !== 'total' && value !== 'percentage') {
      this.setChartOption('showAsStacked', false);
    }
    // make sure if we are changing aggregation that we have a suitable graph type
    const currentReport = this.getReport(this.data.payload);

    // This is required otherwise the dropdown list will not update
    this.graphTypeOptions = [];
    setTimeout(() => {
      this.graphTypeOptions = currentReport.getGraphTypes().map((chartType) => {
        const chartTypeValidity = this.checkGraphType(chartType);

        return {
          title: this.chartOptionDisplayName(chartType),
          id: chartType,
          disabled: !chartTypeValidity.valid,
          subtitle: chartTypeValidity.message,
        };
      });
    });

    const idx = currentReport.getGraphTypes().findIndex((g) => g === this.data.payload.graphType);
    // if not, switch to the first allowed
    if (-1 === idx) {
      this.data.payload.graphType = currentReport.getGraphTypes()[0];
    }

    this.initTimeIntervals(currentReport);
    this.canShowAsStacked = currentReport.canShowAsStacked();
    this.canSetChartMaximum = currentReport.canSetChartMaximum();
    this.canSetChartGoal = currentReport.canSetChartGoal();

    this.setDisplayChartValuesOptions(currentReport);
    this.setColorByPointOptions(currentReport);

    this.setDateMarkers(currentReport);
  }

  public onDateIntervalChange(value: ChartDateInterval) {
    this.setChartOption('dateInterval', value, true);

    if (value === 'week' && typeof this.data.payload.showWeekPrefix === 'undefined') {
      this.setChartOption('showWeekPrefix', true, false);
    }
  }

  setChartGoalOption(goal: string, input?: HTMLInputElement): void {
    const decimalPointIndex = goal.indexOf('.');
    if (decimalPointIndex !== -1 && !(goal.startsWith('.') || goal.endsWith('.'))) {
      goal = goal.substring(0, decimalPointIndex + 2);
    }
    if (input) {
      input.value = goal;
    }

    this.setChartOption('chartGoal', goal);
    this.validateChartGoalOptions();
  }

  private setChartGoalColor(color: RGBAColor): void {
    this.chartGoalColor = color;
    this.setChartOption(
      'chartGoalColor',
      `rgba(${color.red},${color.green},${color.blue},${color.alpha})`,
      true
    );
  }

  public getReport(payload = this.data.payload): AbstractChartReport {
    const reportOptions = JSON.parse(JSON.stringify(payload));
    reportOptions['widgetDataSource'] = this.data.widgetDataSource;
    return this.chartReportService.configureReport(reportOptions);
  }

  public chartOptionDisplayName(chartType: string): string {
    const translations = translateObject('widgets.options.display-names');
    switch (chartType) {
      case 'percentage':
        if (this.data.payload.analyticItem === 'documents') {
          return translations['percent-of-documents'];
        } else {
          return translations['documents-by-percentage'];
        }
      default:
        return translations[chartType] ?? chartType;
    }
  }

  checkGraphType(chartType) {
    if (chartType === 'pie') {
      if (this.isPriorCurrentVolumeChart) {
        return {
          valid: false,
          message: translate('widgets.options.cannot-switch-pie-ipwg'),
        };
      }

      if (this.data.payload.aggregator === 'percentage') {
        return {
          valid: false,
          message: translate('widgets.options.cannot-switch-pie-percentage'),
        };
      }

      if (this.data.comparison?.filters.length > 1) {
        return {
          valid: false,
          message: translate('widgets.options.cannot-switch-pie-comparison'),
        };
      }
    }

    return {
      valid: true,
    };
  }

  checkAggregator(aggregation) {
    if (this.isOverTimeAggregation(aggregation)) {
      if (
        !this.data.widgetDataSource.hasDateData ||
        !this.currentReport.options.widgetDataSource.hasDateData
      ) {
        return {
          valid: false,
          message: translate('widgets.options.datasource-has-no-date'),
        };
      }

      if (this.data.payload.isDrilledDown) {
        return {
          valid: false,
          message: translate('widgets.options.cannot-switch-overtime-drilldown'),
        };
      }

      if (this.data.payload.graphType === 'pie') {
        return {
          valid: false,
          message: translate('widgets.options.cannot-switch-overtime-pie'),
        };
      }

      if (this.isPriorCurrentVolumeChart) {
        return {
          valid: false,
          message: translate('widgets.options.cannot-switch-overtime-ipwg'),
        };
      }

      if (this.data.comparison?.filters.length > 1) {
        return {
          valid: false,
          message: translate('widgets.options.cannot-switch-overtime-comparison'),
        };
      }
    }

    if (aggregation.includes('percentage') && this.data.payload.graphType === 'pie') {
      return {
        valid: false,
        message: translate('widgets.options.cannot-switch-percentage-pie'),
      };
    }

    return {
      valid: true,
    };
  }

  isDateIntervalEnabled(value, aggregator = this.data.payload.aggregator) {
    if (!this.data.widgetDataSource.hasDateData) {
      return false;
    }
    if (!this.isOverTimeAggregation(aggregator)) {
      return false;
    }
    const ec = this.data.elementComponent as any;
    if (ec && typeof ec.isDateIntervalEnabled === 'function') {
      return ec.isDateIntervalEnabled(value);
    }
    return true;
  }

  isOverTimeAggregation(agg: string): boolean {
    return (
      this.data.payload.type === 'numericMetadata' ||
      this.data.payload.type === 'journey' ||
      ChartOptionUtils.isOverTimeAggregator(agg)
    );
  }

  onCancel() {
    this.modalClosed.emit(null);
  }

  onSave() {
    if (this.canSetDateMarkers) {
      this.data.payload.dateMarkers = this.dateMarkers.controls.map((c) => ({
        date: moment(c.value.date)
          .tz(this.timezoneService.timezone, true)
          .startOf('day')
          .format(DATE_WITHOUT_TIMEZONE_FORMAT),
        displayName: c.value.displayName,
        color: c.value.color,
      }));

      this.data.needRefresh = true;
    }

    this.modalClosed.emit(this.data);
  }

  onAddDateMarker(dateMarker?: DateMarker): void {
    const dateMarkerGroup = this.formBuilder.group({
      date: new FormControl(null, Validators.required),
      displayName: [''],
      color: [{ red: 0, green: 0, blue: 0, alpha: 1 } as RGBAColorInput],
    });

    if (dateMarker) {
      dateMarkerGroup.patchValue({
        date: moment(dateMarker.date, DATE_WITHOUT_TIMEZONE_FORMAT).toDate(),
        displayName: dateMarker.displayName,
        color: dateMarker.color,
      });
    }

    this.dateMarkers.push(dateMarkerGroup);
  }

  onRemoveDateMarker(index: number): void {
    this.dateMarkers.removeAt(index);
  }

  private initDateMarkers(): void {
    this.dateMarkerForm = this.formBuilder.group({
      dateMarkers: this.formBuilder.array<FormGroup>([]),
    });

    this.data.payload.dateMarkers?.forEach((dm) => this.onAddDateMarker(dm));

    this.dateMarkerColorChange$
      .pipe(
        debounceTime(100),
        distinctUntilChanged((prev, curr) => Utils.deepEquals(prev, curr)),
        takeUntil(this.destroy$)
      )
      .subscribe((e) => this.dateMarkers.controls[e.index].controls.color.patchValue(e.color));
  }

  private setDateMarkers(report: AbstractChartReport): void {
    this.canSetDateMarkers = report.canSetDateMarkers();

    if (!this.canSetDateMarkers) {
      this.dateMarkers.clear();
    }
  }

  private get isDateMarkersInvalid(): boolean {
    if (!this.canSetDateMarkers) {
      return false;
    }

    return this.dateMarkerForm.invalid;
  }

  get dateMarkers(): FormArray<
    FormGroup<{
      date: FormControl<Date>;
      displayName: FormControl<string>;
      color: FormControl<RGBAColorInput>;
    }>
  > {
    return this.dateMarkerForm.controls.dateMarkers;
  }

  get isAddDateMarkerDisabled(): boolean {
    return this.dateMarkerForm.invalid || this.dateMarkers.controls.length >= MAX_DATE_MARKER;
  }

  get isDefaultPrimaryAxis(): boolean {
    return this.chartAxisValue === 'default';
  }

  get isDefaultSecondaryAxis(): boolean {
    return this.chartSecondaryAxisValue === 'default';
  }

  get showAxes(): boolean {
    return [
      'documentCount',
      'breakdown',
      'sentimentScore',
      'sentimentPolarity',
      'sentimentVolume',
    ].includes(this.data.payload.type);
  }

  validatePrimaryAxisValues(): void {
    this.validateAxisValues(
      this.data.payload.chartMinimum,
      this.data.payload.chartMaximum,
      this.data.payload.chartInterval,
      { minMax: 'maxHigherThanMin', interval: 'intervalTooLarge' }
    );
  }

  validateSecondaryAxisValues(): void {
    this.validateAxisValues(
      this.data.payload.chartSecondaryMinimum,
      this.data.payload.chartSecondaryMaximum,
      this.data.payload.chartSecondaryInterval,
      { minMax: 'maxHigherThanMinSecondary', interval: 'intervalTooLargeSecondary' }
    );
  }

  private validateAxisValues(
    min,
    max,
    interval,
    errorKeys: { minMax: string; interval: string }
  ): void {
    this.errorMessages.delete(errorKeys.minMax);
    this.errorMessages.delete(errorKeys.interval);

    const minValue = parseFloat(min);
    const maxValue = parseFloat(max);
    const intervalValue = parseFloat(interval);

    if (!isNaN(minValue) && !isNaN(maxValue) && minValue >= maxValue) {
      this.errorMessages.set(errorKeys.minMax, translate('widgets.options.error-min-max'));
    }

    if (
      !isNaN(minValue) &&
      !isNaN(maxValue) &&
      !isNaN(intervalValue) &&
      intervalValue >= maxValue - minValue
    ) {
      this.errorMessages.set(errorKeys.interval, translate('widgets.options.error-interval'));
    }
  }

  setChartAxisValueOption(value: ChartAxisValues): void {
    this.setChartOption(nameof<WidgetChartOptions>('chartAxisValue'), value);
    this.chartAxisValue = value;

    if (value === 'default') {
      this.setChartOption(nameof<WidgetChartOptions>('chartMinimum'), undefined);
      this.setChartOption(nameof<WidgetChartOptions>('chartMaximum'), undefined);
      this.setChartOption(nameof<WidgetChartOptions>('chartInterval'), undefined);
      this.validatePrimaryAxisValues();
    }
  }

  setChartAxisSecondaryValueOption(value: ChartAxisValues): void {
    this.setChartOption(nameof<WidgetChartOptions>('chartSecondaryAxisValue'), value);
    this.chartSecondaryAxisValue = value;

    if (value === 'default') {
      this.setChartOption(nameof<WidgetChartOptions>('chartSecondaryMinimum'), undefined);
      this.setChartOption(nameof<WidgetChartOptions>('chartSecondaryMaximum'), undefined);
      this.setChartOption(nameof<WidgetChartOptions>('chartSecondaryInterval'), undefined);
      this.validateSecondaryAxisValues();
    }
  }

  private setDisplayChartValuesOptions(currentReport: AbstractChartReport): void {
    this.canDisplayChartValues = currentReport.canDisplayChartValues();
    this.isDisplayChartValuesDisabled = currentReport.isDisplayChartValuesDisabled();
    this.displayChartValuesDisabledTooltip = currentReport.displayChartValuesDisabledTooltip();

    if (
      (!this.canDisplayChartValues || this.isDisplayChartValuesDisabled) &&
      this.data.payload.displayChartValues
    ) {
      this.setChartOption('displayChartValues', false);
    }
  }

  private setColorByPointOptions(currentReport: AbstractChartReport): void {
    this.canColorByPoint = currentReport.canColorByPoint();
    this.isColorByPointDisabled = currentReport.isColorByPointDisabled();
    this.colorByPointDisabledTooltip = currentReport.colorByPointDisabledTooltip();

    if ((!this.canColorByPoint || this.isColorByPointDisabled) && this.data.payload.colorByPoint) {
      this.setChartOption('colorByPoint', false);
    }
  }

  onSecondaryAxisToggle(isToggled: boolean) {
    this.data.payload.hasSecondaryAxis = isToggled;
    this.data.needRefresh = true;
  }

  setChartSecondaryOption(prop: string, value) {
    this.setChartOption(prop, value);
    this.validateSecondaryAxisValues();
  }
}
